import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

/**
 * Custom hook for managing likes on shorts videos
 * Phase 1: Local storage + Supabase (no authentication required)
 */
const useShortsLikes = (shortUrl) => {
  const [likeCount, setLikeCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [loading, setLoading] = useState(false);

  // Local storage key for tracking liked videos
  const LIKED_VIDEOS_KEY = 'shorts_liked_videos';

  // Get liked videos from localStorage
  const getLikedVideos = useCallback(() => {
    try {
      const likedVideos = localStorage.getItem(LIKED_VIDEOS_KEY);
      return likedVideos ? JSON.parse(likedVideos) : [];
    } catch (err) {
      console.error('Error reading liked videos from localStorage:', err);
      return [];
    }
  }, []);

  // Save liked videos to localStorage
  const saveLikedVideos = useCallback((likedVideos) => {
    try {
      localStorage.setItem(LIKED_VIDEOS_KEY, JSON.stringify(likedVideos));
    } catch (err) {
      console.error('Error saving liked videos to localStorage:', err);
    }
  }, []);

  // Fetch current like count from Supabase
  const fetchLikeCount = useCallback(async () => {
    if (!shortUrl) return;

    try {
      const { data, error } = await supabase
        .from('shorts_likes')
        .select('like_count')
        .eq('short_url', shortUrl)
        .single();

      if (error) {
        // Silently handle common errors
        if (error.code === 'PGRST116') { // No rows found
          setLikeCount(0);
        } else if (error.code === '42501') { // RLS policy violation
          console.log('Supabase RLS not configured for likes - using local storage only');
          setLikeCount(0);
        } else {
          console.warn('Error fetching like count:', error);
        }
        return;
      }

      setLikeCount(data?.like_count || 0);
    } catch (err) {
      console.warn('Error in fetchLikeCount:', err);
      setLikeCount(0); // Default to 0 on error
    }
  }, [shortUrl]);

  // Check if user has liked this video
  const checkHasLiked = useCallback(() => {
    if (!shortUrl) return;

    const likedVideos = getLikedVideos();
    setHasLiked(likedVideos.includes(shortUrl));
  }, [shortUrl, getLikedVideos]);

  // Toggle like status
  const toggleLike = useCallback(async () => {
    if (!shortUrl || loading) return;

    setLoading(true);
    
    try {
      const likedVideos = getLikedVideos();
      const currentlyLiked = likedVideos.includes(shortUrl);
      const newLikeState = !currentlyLiked;
      
      // Update local state immediately for responsive UI
      setHasLiked(newLikeState);
      
      if (newLikeState) {
        // Add like
        const newLikedVideos = [...likedVideos, shortUrl];
        saveLikedVideos(newLikedVideos);
        setLikeCount(prev => prev + 1);
        
        // Update Supabase (skip if RLS not configured)
        try {
          const { error } = await supabase
            .from('shorts_likes')
            .upsert({
              short_url: shortUrl,
              like_count: likeCount + 1
            }, {
              onConflict: 'short_url'
            });
          
          if (error && error.code !== '42501') { // Ignore RLS errors
            console.warn('Error adding like to Supabase:', error);
            // Don't revert for RLS errors - keep local state
            if (error.code !== '42501') {
              // Revert optimistic update on other errors
              setHasLiked(false);
              setLikeCount(prev => prev - 1);
              const revertedLikedVideos = likedVideos.filter(url => url !== shortUrl);
              saveLikedVideos(revertedLikedVideos);
            }
          }
        } catch (err) {
          console.log('Supabase update skipped - using local storage only');
        }
      } else {
        // Remove like
        const newLikedVideos = likedVideos.filter(url => url !== shortUrl);
        saveLikedVideos(newLikedVideos);
        setLikeCount(prev => Math.max(0, prev - 1));
        
        // Update Supabase (skip if RLS not configured)
        try {
          const newCount = Math.max(0, likeCount - 1);
          const { error } = await supabase
            .from('shorts_likes')
            .upsert({
              short_url: shortUrl,
              like_count: newCount
            }, {
              onConflict: 'short_url'
            });
          
          if (error && error.code !== '42501') { // Ignore RLS errors
            console.warn('Error removing like from Supabase:', error);
            // Don't revert for RLS errors - keep local state
            if (error.code !== '42501') {
              // Revert optimistic update on other errors
              setHasLiked(true);
              setLikeCount(prev => prev + 1);
              const revertedLikedVideos = [...likedVideos, shortUrl];
              saveLikedVideos(revertedLikedVideos);
            }
          }
        } catch (err) {
          console.log('Supabase update skipped - using local storage only');
        }
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    } finally {
      setLoading(false);
    }
  }, [shortUrl, loading, likeCount, getLikedVideos, saveLikedVideos]);

  // Initialize data when shortUrl changes
  useEffect(() => {
    if (shortUrl) {
      fetchLikeCount();
      checkHasLiked();
    } else {
      setLikeCount(0);
      setHasLiked(false);
    }
  }, [shortUrl, fetchLikeCount, checkHasLiked]);

  return {
    likeCount,
    hasLiked,
    loading,
    toggleLike
  };
};

export default useShortsLikes;